import { useLeedflowOverviewStats } from '../../../hooks/leedflow/useLeedflowOverviewStats';
import { useLeedflowLiveStats } from '../../../hooks/leedflow/useLeedflowLiveStats';
import { Alert, Button, Tabs } from 'antd';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import OverviewFilters from '../leedflow/OverviewFilters';
import GeneralTab from '../leedflow/tabs/GeneralTab';
import ReliabilityTab from '../leedflow/tabs/ReliabilityTab';
import PublicationTab from '../leedflow/tabs/PublicationTab';

export enum LeedflowOverviewTab {
  GENERAL = 'general',
  RELIABILITY = 'reliability',
  PUBLICATION = 'publication',
}

const LeedflowOverview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || LeedflowOverviewTab.GENERAL;

  // Shared filters — apply to the General and Reliability tabs.
  const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<string[]>([]);
  const [excludes, setExcludes] = useState<string[]>([]);

  const [isConfidential] = useState(false);

  const activeSubscriptionIds = selectedSubscriptionIds.length > 0 ? selectedSubscriptionIds : undefined;
  // Une sélection d'abonnements prime sur l'exclusion : choisir un compte
  // interne en l'excluant rendrait un tableau vide. Le backend applique la
  // même règle (`readExclude`).
  const activeExclude = !activeSubscriptionIds && excludes.length > 0 ? excludes : undefined;

  const { stats, status, subscriptions } = useLeedflowOverviewStats(period, activeSubscriptionIds, activeExclude);
  const { stats: live, status: liveStatus } = useLeedflowLiveStats(activeSubscriptionIds, activeExclude);

  // Le tableau de bord ouvre sur « Aujourd'hui », et à 7 h du matin
  // « Aujourd'hui » est légitimement vide. Sans explication, une page à zéro se
  // lit comme une panne : on recharge, on doute des chiffres, on finit par
  // ouvrir la base. La bannière dit que le vide est normal et propose le seul
  // geste utile.
  //
  // Elle ne bascule pas toute seule : changer la période dans le dos de
  // l'utilisateur lui ferait lire les chiffres d'hier en croyant voir
  // aujourd'hui, ce qui est pire que le vide.
  const isToday =
    !!period[0] && !!period[1] && period[0].isSame(dayjs(), 'day') && period[1].isSame(dayjs(), 'day');

  const hasNoData =
    stats.callsStats.handledCalls === 0 &&
    stats.callsStats.faulty.total === 0 &&
    stats.smsStats.sentCount + stats.smsStats.receivedCount === 0;

  // `status !== LOADING` seulement : pendant le chargement, tous les compteurs
  // valent zéro et la bannière clignoterait à chaque changement de période.
  const [isEmptyNoticeDismissed, setIsEmptyNoticeDismissed] = useState(false);
  const showEmptyNotice =
    isToday && hasNoData && status !== ContextStatus.LOADING && !isEmptyNoticeDismissed;

  const selectYesterday = () => {
    setPeriod([dayjs().add(-1, 'd').startOf('day'), dayjs().add(-1, 'd').endOf('day')]);
    setIsEmptyNoticeDismissed(true);
  };

  // Depuis la carte : un clic sur un utilisateur ajoute son abonnement au
  // filtre. Ajout et non remplacement — on empile les zones qu'on inspecte —
  // et sans doublon, le Select refuserait deux fois la même valeur.
  const addSubscriptionToFilter = (subscriptionId: string) => {
    setSelectedSubscriptionIds((previous) =>
      previous.includes(subscriptionId) ? previous : [...previous, subscriptionId],
    );
  };

  const onTabChange = (key: string) => {
    setSearchParams(prev => {
      prev.set('tab', key);
      return prev;
    });
  };

  return (
    <>
      <div style={{ marginTop: '2rem' }}>
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
          <OverviewFilters
            period={period}
            onPeriodChange={setPeriod}
            subscriptionIds={selectedSubscriptionIds}
            onSubscriptionIdsChange={setSelectedSubscriptionIds}
            excludes={excludes}
            onExcludesChange={setExcludes}
            subscriptions={subscriptions}
            loading={status === ContextStatus.LOADING}
          />
        </div>
      </div>

      {showEmptyNotice && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => setIsEmptyNoticeDismissed(true)}
          style={{ marginBottom: '1rem' }}
          message="Aucune activité aujourd'hui pour le moment"
          description="Rien n'a encore été enregistré depuis minuit. Ce n'est pas forcément une anomalie — il est peut-être simplement trop tôt."
          action={
            <Button size="small" type="primary" onClick={selectYesterday}>
              Voir hier
            </Button>
          }
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: LeedflowOverviewTab.GENERAL,
            label: 'Général',
            children: (
              <GeneralTab
                stats={stats}
                period={period}
                subscriptionIds={activeSubscriptionIds}
                exclude={activeExclude}
                onSelectSubscription={addSubscriptionToFilter}
                status={status}
                live={live}
                liveStatus={liveStatus}
                isConfidential={isConfidential}
              />
            ),
          },
          {
            key: LeedflowOverviewTab.RELIABILITY,
            label: 'Fiabilité',
            children: (
              <ReliabilityTab
                period={period}
                subscriptionIds={activeSubscriptionIds}
                exclude={activeExclude}
                faulty={stats.callsStats.faulty}
                faultyRows={stats.faultyCalls}
                faultyStatus={status}
              />
            ),
          },
          {
            key: LeedflowOverviewTab.PUBLICATION,
            label: 'Publication',
            // Les filtres du haut (période, abonnements) ne s'y appliquent pas : un état de
            // store est celui de maintenant, pas celui d'une période.
            children: <PublicationTab />,
          },
        ]}
      />
    </>
  );
};

export default LeedflowOverview;
